import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  IconButton, Grid, Paper, Typography, Box,
  styled, alpha, useTheme, LinearProgress, Alert,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import Photos from './Photos';
import API_BASE_URL from '../config';
import { useNavigate } from 'react-router-dom';
import UpgradeModal from '../components/UpgradeModal';
import useAccess from '../hooks/useAccess';
import { getErrorMessage } from '../utils/apiError';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
}));

const UploadSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: theme.spacing(2),
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  backgroundColor: alpha(theme.palette.primary.main, 0.02),
}));

const PreviewImage = styled('img')(({ theme }) => ({
  width: '100%', height: 120, objectFit: 'cover', borderRadius: theme.spacing(1),
}));

const GalleryAdd = ({ userId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isPremium, isFree, loading: accessLoading } = useAccess();

  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const idRef = useRef(null);

  const fetchPhotos = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/photos`, { params: { userId: uid }, withCredentials: true });
      setPhotos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err.response?.status === 403) setUpgradeOpen(true);
      else if (err.response?.status !== 404) console.error('Error fetching photos:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        let uid = userId;
        if (!uid) {
          const res = await axios.get(`${API_BASE_URL}/user/id`, { withCredentials: true });
          uid = res.data.userId;
        }
        idRef.current = uid;
        fetchPhotos(uid);
      } catch (err) {
        if (err.response?.status === 401) navigate('/login');
      }
    };
    init();
  }, [userId, fetchPhotos, navigate]);

  useEffect(() => {
    const urls = Array.from(selectedFiles).map(f => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [selectedFiles]);

  const handleFileChange = (e) => {
    if (isFree) { setUpgradeOpen(true); return; }
    const files = e.target.files;
    if (!files?.length) return;
    handleUpload(files);
  };

  const handleUpload = async (files) => {
    if (!files?.length || !idRef.current) return;
    const formData = new FormData();
    for (const file of files) {
      formData.append('photos', file);
      formData.append('userId', idRef.current);
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadError('');
    setSelectedFiles([]);
    try {
      await axios.post(`${API_BASE_URL}/photos`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total)); },
      });
      await fetchPhotos(idRef.current);
    } catch (err) {
      if (err.response?.status === 403) setUpgradeOpen(true);
      else setUploadError(getErrorMessage(err));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (accessLoading) return null;

  return (
    <Box>
      <StyledPaper elevation={0}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 4 }}>
          Photo Gallery
        </Typography>

        {isFree ? (
          <UploadSection onClick={() => setUpgradeOpen(true)} sx={{ cursor: 'pointer' }}>
            <LockIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Gallery is a Premium feature. Click to upgrade.
            </Typography>
          </UploadSection>
        ) : (
          <UploadSection>
            <input type="file" multiple hidden id="upload" onChange={handleFileChange} />
            <label htmlFor="upload">
              <IconButton color="primary" component="span" disabled={uploading}
                sx={{ fontSize: 48, p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                <PhotoCamera fontSize="large" />
              </IconButton>
            </label>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {uploading ? `Uploading ${uploadProgress}%...` : 'Click to select and upload photos'}
            </Typography>
            {uploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 2, borderRadius: 4, height: 8 }} />}
            {uploadError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setUploadError('')}>{uploadError}</Alert>}
          </UploadSection>
        )}

        {previews.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>Selected Previews</Typography>
            <Grid container spacing={2}>
              {previews.map((p, i) => (
                <Grid item xs={6} sm={4} md={2} key={i}>
                  <Box sx={{ position: 'relative' }}>
                    <PreviewImage src={p} alt="" loading="lazy" />
                    <IconButton size="small"
                      onClick={() => { const arr = Array.from(selectedFiles); arr.splice(i, 1); setSelectedFiles(arr); }}
                      sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(255,255,255,0.8)' }}>
                      <CancelIcon color="error" fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {!isFree && <Photos photos={photos} fetchPhotos={() => fetchPhotos(idRef.current)} />}
      </StyledPaper>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} featureLabel="Photo Gallery" />
    </Box>
  );
};

export default GalleryAdd;
